import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useMe, can } from "@/context/MeContext";
import { PageHeader } from "@/components/PageHeader";
import { COLLECTION_LABELS } from "@/lib/sp";

const when = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");

export default function SyncPage() {
  const api = useApi();
  const me = useMe();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("get", "/sync/status").then(setD).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true);
    try { const s = await api("post", "/sync/run"); s.last_error ? toast.error(s.last_error) : toast.success("Synchronisation terminée"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return <p className="text-sm text-stone-500">Chargement…</p>;
  const s = d.state;
  return (
    <div className="space-y-8" data-testid="sync-page">
      <PageHeader title="Synchronisation" subtitle="Les données sont lues en continu depuis l'application Sehatic-Pedia, et chaque modification faite ici y est envoyée immédiatement.">
        {can(me, "sync") && <Button onClick={run} disabled={busy} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid="sync-run-button"><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Synchroniser maintenant</Button>}
      </PageHeader>
      <div className="bg-white border border-stone-200 rounded-lg p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm" data-testid="sync-state">
        <div><p className="text-stone-500">État</p><p className={`font-medium flex items-center gap-1.5 mt-1 ${s.last_error ? "text-red-700" : "text-green-700"}`} data-testid="sync-health">{s.last_error ? <><AlertTriangle className="h-4 w-4" /> Erreur</> : <><CheckCircle2 className="h-4 w-4" /> Connecté</>}</p></div>
        <div><p className="text-stone-500">Dernière lecture</p><p className="font-medium mt-1" data-testid="sync-last-pull">{when(s.last_pull_at)}</p></div>
        <div><p className="text-stone-500">Dernière nouveauté reçue</p><p className="font-medium mt-1">{when(s.last_change_at)}</p></div>
        <div><p className="text-stone-500">Modifications reçues</p><p className="font-medium mt-1">{s.pulled_total ?? 0} (position {s.cursor ?? 0})</p></div>
        {s.last_error && <p className="sm:col-span-2 lg:col-span-4 text-red-700" data-testid="sync-error">{s.last_error} ({when(s.last_error_at)})</p>}
        <p className="sm:col-span-2 lg:col-span-4 text-xs text-stone-500">Source : {d.source}. Lecture automatique à chaque ouverture de page (au plus toutes les 15 secondes) et toutes les 15 minutes en arrière-plan.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="sync-counts">
          <h3 className="font-heading text-base font-bold text-stone-900">Données synchronisées</h3>
          <div className="mt-3 divide-y divide-stone-100 text-sm">
            {Object.entries(d.counts).map(([k, v]) => <div key={k} className="flex py-2"><span className="text-stone-700">{COLLECTION_LABELS[k] || k}</span><span className="ml-auto font-medium" data-testid={`sync-count-${k}`}>{v}</span></div>)}
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="sync-log">
          <h3 className="font-heading text-base font-bold text-stone-900">Modifications envoyées à l'application</h3>
          <div className="mt-3 divide-y divide-stone-100 text-sm">
            {d.log.length === 0 && <p className="py-2 text-stone-500">Aucune modification envoyée pour l'instant.</p>}
            {d.log.map((l, i) => <div key={i} className="py-2 flex flex-wrap gap-x-4"><span className="font-medium">{l.action}</span><span>{COLLECTION_LABELS[l.collection] || l.collection}{l.label ? ` · ${l.label}` : ""}</span><span className="text-stone-500">{l.by}</span><span className="ml-auto text-stone-500">{fmtDate(l.at?.slice(0, 16))}</span></div>)}
          </div>
        </div>
      </div>
    </div>
  );
}
